import { Badge, Card, Group, Stack, Table, Text } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconClock, IconX } from '@tabler/icons-react';

const VERDICT_BADGES = {
  ok: { color: 'green', label: 'OK', icon: IconCheck },
  partial: { color: 'yellow', label: 'Partly applied', icon: IconAlertTriangle },
  failed: { color: 'red', label: 'Failed', icon: IconX },
  timeout: { color: 'orange', label: 'Meta did not finish', icon: IconClock },
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
};

const formatCount = (value) => (value === null || value === undefined ? '-' : Number(value).toLocaleString());

const VerdictBadge = ({ verdict }) => {
  const badge = VERDICT_BADGES[verdict] || { color: 'gray', label: verdict || 'Unknown', icon: null };
  const Icon = badge.icon;
  return (
    <Badge color={badge.color} variant="light" leftSection={Icon ? <Icon size={12} /> : null}>
      {badge.label}
    </Badge>
  );
};

const FacebookCatalogUploadStatus = ({ facebookAccount, isSuperAdmin = false }) => {
  const uploadedAt = formatDate(facebookAccount?.lastFeedUploadAt);
  if (!uploadedAt) {
    return (
      <Text size="sm" c="dimmed" mt="xs">
        No verified catalog upload yet.
      </Text>
    );
  }

  const summary = facebookAccount.lastFeedUploadSummary || {};
  const results = summary.results || [];
  const problemResults = results.filter(
    (result) => result.verdict !== 'ok' && (result.reason || result.topErrors?.length > 0)
  );
  const hasProductCounts = summary.mainFeedProductCount != null || summary.catalogProductCount != null;

  return (
    <Card withBorder mt="sm" data-testid="facebook-catalog-upload-status">
      <Stack gap="xs">
        <Group gap="xs">
          <Text fw={500}>Last catalog upload</Text>
          <VerdictBadge verdict={facebookAccount.lastFeedUploadVerdict} />
          <Text size="sm" c="dimmed">
            {uploadedAt}
          </Text>
        </Group>

        {summary.reason && <Text size="sm">{summary.reason}</Text>}
        {isSuperAdmin && summary.error && (
          <Text size="sm" c="red">
            {summary.error}
          </Text>
        )}

        {hasProductCounts && (
          <Text size="sm" c="dimmed">
            Products in main feed: {formatCount(summary.mainFeedProductCount)} · Products in catalog:{' '}
            {formatCount(summary.catalogProductCount)}
          </Text>
        )}

        {results.length > 0 && (
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Feed</Table.Th>
                <Table.Th>Result</Table.Th>
                <Table.Th>Rows sent</Table.Th>
                <Table.Th>Applied</Table.Th>
                <Table.Th>Rejected</Table.Th>
                <Table.Th>Deleted</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {results.map((result) => (
                <Table.Tr key={`${result.label}-${result.uploadId}`}>
                  <Table.Td>{result.label}</Table.Td>
                  <Table.Td>
                    <VerdictBadge verdict={result.verdict} />
                  </Table.Td>
                  <Table.Td>{formatCount(result.rowsSent)}</Table.Td>
                  <Table.Td>{formatCount(result.applied)}</Table.Td>
                  <Table.Td>{formatCount(result.invalid)}</Table.Td>
                  <Table.Td>{formatCount(result.deleted)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        {problemResults.map((result) => (
          <Stack key={`errors-${result.label}-${result.uploadId}`} gap={2}>
            <Text size="sm" fw={500}>
              Problems in {result.label}
            </Text>
            {result.reason && <Text size="sm">{result.reason}</Text>}
            {(result.topErrors || []).map((cause) => (
              <Text key={cause.summary} size="sm" c="dimmed">
                {cause.summary}: {formatCount(cause.count)}
                {cause.sampleRows?.length > 0 ? ` (rows ${cause.sampleRows.join(', ')})` : ''}
              </Text>
            ))}
          </Stack>
        ))}
      </Stack>
    </Card>
  );
};

export default FacebookCatalogUploadStatus;
